import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { getRepositorio } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  FolderOpen,
  Search,
  Loader2,
  FileText,
  ArrowUpDown,
  ExternalLink,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RepositorioPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('uploaded_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const response = await getRepositorio();
      setFiles(response.data);
    } catch (error) {
      console.error('Error loading repositorio:', error);
      toast.error('Error al cargar el repositorio');
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedFiles = useMemo(() => {
    let filtered = files.filter(f =>
      f.filename.toLowerCase().includes(search.toLowerCase())
    );

    return filtered.sort((a, b) => {
      let valA, valB;
      if (sortBy === 'filename') {
        valA = a.filename.toLowerCase();
        valB = b.filename.toLowerCase();
      } else if (sortBy === 'task') {
        valA = a.task?.sequential_number || '';
        valB = b.task?.sequential_number || '';
      } else {
        valA = a.uploaded_at || '';
        valB = b.uploaded_at || '';
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [files, search, sortBy, sortOrder]);

  const SortHeader = ({ field, label }) => (
    <button
      onClick={() => toggleSort(field)}
      className="flex items-center gap-1 hover:text-slate-700 transition-colors"
      data-testid={`sort-${field}`}
    >
      {label}
      {sortBy === field && (
        <ArrowUpDown className={`w-3 h-3 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
      )}
    </button>
  );

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="repositorio-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
            Repositorio
          </h1>
          <p className="text-slate-500 mt-1">{sortedFiles.length} documentos</p>
        </div>

        {/* Search */}
        <Card className="mb-6 bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar por nombre de archivo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 border-stone-200"
                data-testid="repo-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Files Table */}
        <Card className="bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : sortedFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <FolderOpen className="w-12 h-12 mb-3 text-slate-300" />
                <p className="text-lg font-medium">
                  {search ? 'Sin resultados' : 'No hay documentos'}
                </p>
                <p className="text-sm">
                  {search ? 'Intenta con otro termino de busqueda' : 'Los documentos subidos en tareas apareceran aqui'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                        <SortHeader field="filename" label="Documento" />
                      </th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                        <SortHeader field="task" label="Tarea" />
                      </th>
                      <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                        <SortHeader field="uploaded_at" label="Fecha subida" />
                      </th>
                      <th className="p-4 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {sortedFiles.map((file, index) => (
                      <tr
                        key={`${file.safe_name}-${index}`}
                        className="hover:bg-stone-50 transition-colors group"
                        data-testid={`repo-file-${index}`}
                      >
                        <td className="p-4">
                          <a
                            href={`${API_URL}${file.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors"
                          >
                            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="truncate max-w-xs">{file.filename}</span>
                          </a>
                        </td>
                        <td className="p-4">
                          {file.task ? (
                            <Link
                              to={`/tasks/${file.task.id}`}
                              className="flex items-center gap-2 text-sm text-slate-600 hover:text-emerald-700 transition-colors"
                            >
                              <span className="font-mono text-xs text-slate-400">{file.task.sequential_number}</span>
                              <span className="truncate max-w-xs">{file.task.title}</span>
                              <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100" />
                            </Link>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-500">
                            {file.uploaded_at
                              ? format(new Date(file.uploaded_at), 'dd MMM yyyy, HH:mm', { locale: es })
                              : '-'
                            }
                          </span>
                        </td>
                        <td className="p-4">
                          <a
                            href={`${API_URL}${file.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Descargar"
                          >
                            <Download className="w-4 h-4 text-slate-400 hover:text-slate-700" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RepositorioPage;
